import { l } from '@manychat/manyui'

import { Filter as FilterType } from 'apps/chat/constants/LiveChatInterfaces'
import { LiveChatNoteTypes } from 'apps/chat/constants/LiveChatNoteTypes'
import { LabelAssignmentFinishedEventSchema } from 'apps/chat/interfaces/labels'
import {
  ThreadsSocketSchema,
  LiveChatNote,
  LCSentSocketModel,
} from 'apps/chat/interfaces/threadsSocketSchema'
import { labelAssignmentFinished } from 'apps/chat/redux/labelsSlice'
import { getSelectedLabelId } from 'apps/chat/selectors/labelsSelectors'
import { ChatMessageType } from 'apps/chat/types/chatMessageTypeList'
import { alert } from 'common/core'
import { getUserId } from 'common/core/selectors/appSelectors'
import { getUserRole, UserRoles } from 'common/userRoles'
import {
  anotherTabNotificationsListener,
  userNotificationsListener,
} from 'utils/services/notificationsService'

import { ThreadSuccessBulkText } from '../../constants'
import { threadActions as actions } from '../../reducers/slices/threadListSlice'
import { getCurrentId } from '../../selectors/threadSelectors'

import { updateFilters } from './actions'
import {
  handleThreadUpdatedNotification,
  handleUpdateSubscriberInfo,
  handleThreadMessagedNotification,
  closeAllThreadsNotification,
  openAllThreadsNotification,
  assignThreadsNotification,
  unassignThreadsNotification,
  threadUserReadNotification,
  reloadThreads,
  handleThreadAssignedEvent,
  handleUserThreadNewEvent,
  updateCurrentThreadNotes,
} from './wsHandlers'

// Type guard to check if the model is a LiveChatNote
const isLiveChatNote = (model: LCSentSocketModel): model is LiveChatNote => {
  return Object.values(LiveChatNoteTypes).includes(model.type as LiveChatNoteTypes)
}

anotherTabNotificationsListener.on(ThreadsSocketSchema.threadUpdated, (data, dispatch) => {
  dispatch(handleThreadUpdatedNotification(data.model))
})
anotherTabNotificationsListener.on(ThreadsSocketSchema.threadRead, (data, dispatch) => {
  dispatch(handleThreadUpdatedNotification(data.model))
})
anotherTabNotificationsListener.on(ThreadsSocketSchema.subscriberUpdated, (data, dispatch) => {
  dispatch(handleUpdateSubscriberInfo(data.model))
})
anotherTabNotificationsListener.on(ThreadsSocketSchema.threadMessaged, (data, dispatch) => {
  dispatch(handleThreadMessagedNotification(data.model))
})
anotherTabNotificationsListener.on(ThreadsSocketSchema.threadClosed, (data, dispatch) => {
  dispatch(closeAllThreadsNotification(data.model))
})
anotherTabNotificationsListener.on(ThreadsSocketSchema.threadOpened, (data, dispatch) => {
  dispatch(openAllThreadsNotification(data.model))
})
anotherTabNotificationsListener.on(ThreadsSocketSchema.threadUnassigned, (data, dispatch) => {
  dispatch(unassignThreadsNotification(data.model))
})
anotherTabNotificationsListener.on(ThreadsSocketSchema.threadAssigned, (data, dispatch) => {
  dispatch(assignThreadsNotification(data.model))
})
anotherTabNotificationsListener.on(ThreadsSocketSchema.threadUserRead, (data, dispatch) => {
  dispatch(threadUserReadNotification(data))
})

anotherTabNotificationsListener.on(
  ThreadsSocketSchema.threadsFilterChanged,
  (_, dispatch, getState) => {
    const state = getState() as RootState
    const userRole = getUserRole(state)

    if (userRole !== UserRoles.AGENT) return
    dispatch(reloadThreads())
  },
)

anotherTabNotificationsListener.on(
  ThreadsSocketSchema.accountUserRoleUpdate,
  (data, dispatch, getState) => {
    const state = getState() as RootState
    const currentUserId = getUserId(state)

    if (currentUserId !== data.user_id || data.new_role !== UserRoles.AGENT) return
    dispatch(reloadThreads())
  },
)

anotherTabNotificationsListener.on(ThreadsSocketSchema.lcSent, (data, dispatch) => {
  if (!data.model) return
  if (data.model.type === ChatMessageType.USER_THREAD_NEW) {
    dispatch(handleUserThreadNewEvent(data.model))
  } else if (
    data.model.type === ChatMessageType.ASSIGN ||
    data.model.type === ChatMessageType.REASSIGN
  ) {
    dispatch(handleThreadAssignedEvent(data.model))
  } else if (isLiveChatNote(data.model)) {
    dispatch(updateCurrentThreadNotes(data.model))
  }
})

anotherTabNotificationsListener.on(
  ThreadsSocketSchema.threadBulkActionComplete,
  (data, _, getState) => {
    if (getUserId(getState() as RootState) !== data.user_id || !data.action_type) return

    const message = l.getString(ThreadSuccessBulkText[data.action_type])

    alert(message, 'success')
  },
)

anotherTabNotificationsListener.on(
  ThreadsSocketSchema.threadControlErrorReset,
  (data, dispatch, getState) => {
    const state = getState() as RootState
    const currentThreadId = getCurrentId(state)
    const socketThreadId = data?.model?.subscriber_id
    if (currentThreadId === socketThreadId) {
      dispatch(actions.resetHandoverError())
    }
  },
)

userNotificationsListener.on(LabelAssignmentFinishedEventSchema, (data, dispatch, getState) => {
  dispatch(labelAssignmentFinished(data.label_id))
  const state = getState() as RootState
  if (getSelectedLabelId(state) === data.label_id) {
    dispatch(updateFilters(FilterType.LABEL, data.label_id))
  }
})

export * from './actions'
export * from './wsHandlers'

// all actions from the threadSlice to make this file the main entry point for thread actions
export const {
  dropThreadsList,
  fetchThread,
  fetchThreads,
  fetchNextThreads,
  takeThreadControl,
  markThreadAsRead,
  markThreadAsUnread,
  updateThreadStatus,
  setAssignment,
  toggleBulkSelection,
  checkIsThreadSatisfiesCurrentFilter,
  resetHandoverError,
  setCurrentThread,
  updatedCurrentItemNotification,
  updateStatusFilter,
  updateTagsAndCufsFilter,
  updateConversationsFilter,
  updateTeamMembersFilter,
  updateChannelsFilter,
  updateThreadsSorting,
  setConversationWindowStatus,
  increaseTotalThreadsCount,
  decreaseTotalThreadsCount,
  updateThreadSubscriberInfo,
  toggleChecked,
  updateSelectionType,
  clearSelection,
  setAssignee,
  processSendMessageError,
  addVoiceConversionPendingState,
  removeVoiceConversionPendingState,
  switchLiveChatChannel,
  resetThreadLoading,
  addPreloadImage,
  deletePreloadImage,
  resetImages,
  resetConversationRoutingError,
  setThreadsRequest,
  setThreadsError,
  setThreadsResponse,
} = actions
